import { useMemo, useState } from 'react';
import { useLookups, useRows } from '@/hooks/useLive';
import { useSiteFilter } from '@/hooks/useSiteFilter';
import { save, softDelete, upsert } from '@/lib/crud';
import { date as fmtDate, hours, humanise, num } from '@/lib/format';
import { useAuthStore } from '@/stores/authStore';
import { roleLock } from '@/lib/editability';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusChip } from '@/components/ui/StatusChip';
import { StatTile } from '@/components/ui/StatTile';
import { MachineDrawer } from './machines/MachineDrawer';
import { serviceState } from './machines/serviceState';
import { QUARRY_SITE_ID, worstDoc } from '@/lib/fleet';
import { defectTitle, useOpenDefects } from './fleet/useOpenDefects';
import { meterSuffix, unitOf } from '@/lib/meter';
import type { Machine, MachineType, Setting, Site } from '@/types/models';
import { ExportBar } from '@/components/ui/ExportBar';
import { figures } from '@/lib/export';

type Rag = 'green' | 'amber' | 'red' | 'grey';

const STATUS_TONE: Record<string, Rag | 'blue'> = {
  active: 'green',
  standby: 'grey',
  down: 'red',
  maintenance: 'amber',
  inactive: 'grey',
  disposed: 'grey',
};

export function Machines() {
  const machines = useRows<Machine>('machines', 'fleet_no');
  const types = useRows<MachineType>('machine_types', 'sort_order');
  const settings = useRows<Setting>('settings');
  const lookups = useLookups([{ table: 'machine_types', label: 'name' }]);
  const canEdit = useAuthStore((s) => s.hasRole('manager', 'admin'));
  const role = useAuthStore((s) => s.profile?.role);
  // Everyone may open a record; who may change it is said inside (0046).
  const lock = roleLock(role, ['manager', 'admin'], 'the fleet register');

  const [editing, setEditing] = useState<Machine | Partial<Machine> | null>(null);
  const sites = useRows<Site>('sites', 'name');
  const siteOk = useSiteFilter();
  // The quarry by default; the workshop and management can look at any
  // site, or the whole company.
  const [site, setSite] = useState<string>(QUARRY_SITE_ID);
  const siteName = (id: string | undefined) => (sites ?? []).find((x) => x.id === (id ?? QUARRY_SITE_ID))?.name ?? '—';
  // Vehicles created from the old fuel log, still to be completed (0054).
  const [incomplete, setIncomplete] = useState(false);
  const shown = useMemo(
    () => (machines ?? []).filter((m) => (site === 'all' || (m.site_id ?? QUARRY_SITE_ID) === site)
      && (!incomplete || m.needs_details)),
    [machines, site, incomplete],
  );
  const incompleteCount = useMemo(
    () => (machines ?? []).filter((m) => m.needs_details && (site === 'all' || (m.site_id ?? QUARRY_SITE_ID) === site)).length,
    [machines, site],
  );

  const amberHours = useMemo(() => {
    const s = settings?.find((x) => x.key === 'service_warning_amber_hours');
    return s ? Number(s.value) : 100;
  }, [settings]);
  const amberKm = useMemo(() => {
    const s = settings?.find((x) => x.key === 'service_warning_amber_km');
    return s ? Number(s.value) : 1000;
  }, [settings]);

  const summary = useMemo(() => {
    const active = shown.filter((m) => m.is_active);
    return {
      total: active.length,
      working: active.filter((m) => m.status === 'active').length,
      down: active.filter((m) => m.status === 'down').length,
      servicesDue: active.filter((m) => {
        const s = serviceState(m, amberHours, amberKm);
        return s.rag === 'amber' || s.rag === 'red';
      }).length,
      overdue: active.filter((m) => serviceState(m, amberHours, amberKm).rag === 'red').length,
      docs: active.filter((m) => ['expired', 'due'].includes(worstDoc(m).state)).length,
    };
  }, [shown, amberHours, amberKm]);

  const defects = useOpenDefects();
  const columns: Column<Machine>[] = [
    {
      key: 'fleet_no', label: 'Fleet no.',
      render: (m) => (
        <>
          <strong>{m.fleet_no}</strong>
          {m.needs_details && <> <StatusChip tone="amber">Needs details</StatusChip></>}
          {defects.get(m.id) && (
            <span title={defectTitle(defects.get(m.id)!)} data-testid="open-defects">
              {' '}<StatusChip tone="red">{defects.get(m.id)!.count} open defect{defects.get(m.id)!.count === 1 ? '' : 's'}</StatusChip>
            </span>
          )}
        </>
      ),
    },
    {
      key: 'machine_type_id', label: 'Type',
      value: (m) => lookups.machine_types?.get(m.machine_type_id) ?? '',
      render: (m) => lookups.machine_types?.get(m.machine_type_id) ?? '—',
    },
    {
      key: 'model', label: 'Make & model',
      value: (m) => `${m.manufacturer ?? ''} ${m.model ?? ''}`.trim(),
      render: (m) => `${m.manufacturer ?? ''} ${m.model ?? ''}`.trim() || '—',
    },
    {
      key: 'status', label: 'Status',
      render: (m) => (
        <StatusChip tone={m.is_active ? (STATUS_TONE[m.status] ?? 'grey') : 'grey'}>
          {m.is_active ? humanise(m.status) : 'Inactive'}
        </StatusChip>
      ),
    },
    {
      key: 'current_hour_meter', label: 'Meter', align: 'right',
      value: (m) => m.current_hour_meter,
      render: (m) => (
        <span className="tabular">
          {num(m.current_hour_meter, unitOf(m) === 'km' ? 0 : 1)} {meterSuffix(unitOf(m))}
        </span>
      ),
    },
    {
      key: 'service', label: 'To service', align: 'right',
      value: (m) => serviceState(m, amberHours, amberKm).remaining ?? 99999,
      render: (m) => {
        const s = serviceState(m, amberHours, amberKm);
        if (s.remaining == null) return <span className="muted">—</span>;
        const km = unitOf(m) === 'km';
        const fmt = (x: number) => (km ? num(x, 0) : hours(x));
        const sfx = meterSuffix(unitOf(m));
        return (
          <StatusChip tone={s.rag === 'grey' ? 'grey' : s.rag}>
            {s.remaining < 0 ? `${fmt(Math.abs(s.remaining))} ${sfx} over` : `${fmt(s.remaining)} ${sfx}`}
          </StatusChip>
        );
      },
    },
    {
      key: 'docs', label: 'Documents', sortable: false,
      render: (m) => {
        const w = worstDoc(m);
        if (w.state === 'none' || w.state === 'ok') return <span className="muted">{w.state === 'ok' ? 'in date' : '—'}</span>;
        return (
          <StatusChip tone={w.state === 'expired' ? 'red' : 'amber'}>
            {w.label} {w.state === 'expired' ? 'expired' : `due ${fmtDate(w.date)}`}
          </StatusChip>
        );
      },
    },
    {
      key: 'expected_hours_per_day', label: 'Hrs/day', align: 'right',
      render: (m) => <span className="tabular">{num(m.expected_hours_per_day, 1)}</span>,
    },
  ];
  if (site === 'all') {
    columns.splice(2, 0, {
      key: 'site_id', label: 'Site', value: (m) => siteName(m.site_id), render: (m) => siteName(m.site_id),
    });
  }

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      <div className="row" style={{ gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <select aria-label="Site" className="concrete-branch-select" value={site}
          onChange={(e) => setSite(e.target.value)}>
          <option value="all">All sites</option>
          {(sites ?? []).filter((x) => (x.kind === 'quarry' || x.kind === 'branch') && siteOk(x.id)).map((x) => (
            <option key={x.id} value={x.id}>{x.name}</option>
          ))}
        </select>
        <span className="spacer" />
        <ExportBar title="Fleet register" subtitle={site === 'all' ? 'All sites' : siteName(site)}
          file={`fleet-register-${site === 'all' ? 'all-sites' : siteName(site)}`} landscape disabled={!machines}
          sheets={() => [
            { name: 'Summary', rows: figures([
              ['Machines active', summary.total], ['Working', summary.working], ['Down', summary.down],
              ['Service due', summary.servicesDue], ['Service overdue', summary.overdue],
              ['Documents expired or due', summary.docs]]) },
            { name: 'Machines', rows: shown.map((m) => {
              const sv = serviceState(m, amberHours, amberKm);
              const w = worstDoc(m);
              return {
                'Fleet no.': m.fleet_no, Name: m.name ?? '', Type: lookups.machine_types?.get(m.machine_type_id) ?? '',
                Site: siteName(m.site_id), Manufacturer: m.manufacturer ?? '', Model: m.model ?? '',
                VIN: m.vin ?? '', Registration: m.registration_no ?? '',
                Status: m.is_active ? humanise(m.status) : 'Inactive', Meter: m.current_hour_meter, Unit: unitOf(m),
                'Next service at': sv.next ?? '', 'To service': sv.remaining ?? '',
                'Service interval': m.service_interval_hours ?? '', 'Or every (months)': m.service_interval_months ?? '',
                'Hours a day': m.expected_hours_per_day ?? '', Commissioned: m.commissioned_date ?? '',
                'Licence disc': m.licence_disc_expiry ?? '', COF: m.cof_expiry ?? '', Insurance: m.insurance_expiry ?? '',
                Documents: w.state === 'none' ? '' : w.state === 'ok' ? 'in date' : `${w.label} ${w.state}`,
              };
            }) },
          ]} />
      </div>

      <Panel title={`Fleet summary${site === 'all' ? ' · all sites' : ` · ${siteName(site)}`}`}>
        <div className="grid grid--stats">
          <StatTile label="Machines" value={summary.total} sub="active in the register" />
          <StatTile label="Documents" value={summary.docs} sub="expired or due in 30 days"
            rag={summary.docs ? 'amber' : 'green'} />
          <StatTile label="Working" value={summary.working} rag="green" />
          <StatTile label="Down" value={summary.down} rag={summary.down ? 'red' : 'green'} />
          <StatTile label="Service due" value={summary.servicesDue} rag={summary.servicesDue ? 'amber' : 'green'} />
          <StatTile label="Service overdue" value={summary.overdue} rag={summary.overdue ? 'red' : 'green'} />
        </div>
      </Panel>

      <Panel
        title="Machine register"
        flush
        actions={
          canEdit ? (
            <Button style={{ minHeight: 32 }} onClick={() => setEditing({ site_id: site === 'all' ? QUARRY_SITE_ID : site })}>+ New machine</Button>
          ) : (
            <span className="small muted">Read only for your role</span>
          )
        }
      >
        <div style={{ padding: 'var(--sp-3) var(--sp-3) 0' }} className="small muted">
          Every machine that works or is scheduled to work. Hour meters roll forward
          automatically from the daily diary — you do not maintain them here after the
          opening reading.
        </div>
        <DataTable
          rows={shown}
          columns={columns}
          rowKey={(m) => m.id}
          onRowClick={(m) => setEditing(m)}
          defaultSort="fleet_no"
          actions={incompleteCount > 0 || incomplete ? (
            <label className="row small" style={{ gap: 'var(--sp-1)', alignItems: 'center' }}>
              <input type="checkbox" checked={incomplete} onChange={(e) => setIncomplete(e.target.checked)} />
              Needs details only ({incompleteCount})
            </label>
          ) : undefined}
          searchPlaceholder="Search fleet number, make, model…"
          empty="No machines yet. Add the fleet before capturing diaries."
        />
      </Panel>

      {editing && (
        <MachineDrawer
          machine={editing}
          types={types ?? []}
          amberHours={amberHours}
          amberKm={amberKm}
          locked={lock}
          onClose={() => setEditing(null)}
          onSave={async (values) => {
            const result = await save(
              () => upsert('machines', values as Record<string, unknown> & { id?: string }),
              'Machine saved',
            );
            if (result) setEditing(null);
          }}
          onDelete={async (id) => {
            // A machine with history is refused by the database with the
            // reason and what to do instead; the drawer stays open on it.
            const ok = await save(() => softDelete('machines', id), 'Machine removed');
            if (ok !== null) setEditing(null);
            return ok !== null;
          }}
        />
      )}
    </div>
  );
}
